import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight,
  Search,
  CalendarDays,
  Sparkles,
  Newspaper,
} from "lucide-react";
import useSeo from "../lib/useSeo";
import { buildCanonical } from "../lib/insightsLinks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function Insights() {
  const [featured, setFeatured] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Fetch featured + categories on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fRes, cRes] = await Promise.all([
          axios.get(`${API}/insights/featured`).catch(() => null),
          axios.get(`${API}/insights/categories`),
        ]);
        if (!cancelled) {
          setFeatured(fRes?.data || null);
          setCategories(cRes.data?.categories || []);
        }
      } catch {
        // empty state is acceptable
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch list when filters change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (activeCategory && activeCategory !== "All")
          params.category = activeCategory;
        if (debouncedQ) params.q = debouncedQ;
        const res = await axios.get(`${API}/insights`, { params });
        if (!cancelled) setArticles(res.data?.items || []);
      } catch {
        if (!cancelled) setArticles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, debouncedQ]);

  // Featured article gets pulled out of the main grid when no search/filter active.
  const gridArticles = useMemo(() => {
    if (!featured || activeCategory !== "All" || debouncedQ) return articles;
    return articles.filter((a) => a.slug !== featured.slug);
  }, [articles, featured, activeCategory, debouncedQ]);

  const showFeatured =
    !!featured && activeCategory === "All" && !debouncedQ;

  // ---- SEO ----
  const canonical = buildCanonical("/insights");
  useSeo({
    title:
      "Insights — Linked2Marketing | Considered perspectives on digital infrastructure",
    description:
      "Practical insights on websites, CRM systems, business automation, AI visibility, customer experience, and digital growth — from Linked2Marketing.",
    canonical,
    ogImage: featured?.featured_image,
    ogType: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Linked2Marketing Insights",
      url: canonical,
      description:
        "A long-form authority hub covering AI visibility, business automation, CRM systems, websites, lead generation, customer experience, and case studies.",
      isPartOf: {
        "@type": "WebSite",
        name: "Linked2Marketing",
        url: canonical.replace(/\/insights$/, ""),
      },
      hasPart: (articles || []).slice(0, 12).map((a) => ({
        "@type": "BlogPosting",
        headline: a.title,
        url: buildCanonical(`/insights/${a.slug}`),
        datePublished: a.published_at,
        image: a.featured_image,
        articleSection: a.category,
      })),
    },
  });

  return (
    <div className="pt-32 sm:pt-40 pb-24" data-testid="page-insights">
      {/* HEADER */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <span className="eyebrow text-gold reveal">Insights</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight text-mute-100 reveal reveal-delay-1">
              Considered perspectives on{" "}
              <em className="not-italic text-gradient-gold">
                digital infrastructure
              </em>
              .
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] leading-[1.75] text-mute-200 reveal reveal-delay-2">
              Practical insights on websites, CRM systems, business automation,
              AI visibility, customer experience, and digital growth.
            </p>
          </div>
        </div>

        {/* Search + categories */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-mute-300" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search insights…"
                aria-label="Search insights"
                data-testid="insights-search-input"
                className="w-full rounded-full bg-white/[0.025] border border-white/10 backdrop-blur-md pl-11 pr-4 py-3 text-[14px] text-mute-100 placeholder:text-mute-300 focus:outline-none focus:border-gold/40 focus:bg-gold/[0.04] transition-colors"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <CategoryPill
                label="All"
                count={categories.reduce((s, c) => s + (c.count || 0), 0)}
                active={activeCategory === "All"}
                onClick={() => setActiveCategory("All")}
              />
              {categories.map((c) => (
                <CategoryPill
                  key={c.name}
                  label={c.name}
                  count={c.count}
                  disabled={c.count === 0}
                  active={activeCategory === c.name}
                  onClick={() => setActiveCategory(c.name)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {showFeatured && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-14">
          <FeaturedCard article={featured} />
        </section>
      )}

      {/* GRID */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-16">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h2 className="font-display text-2xl sm:text-3xl text-mute-100 leading-[1.15]">
            {debouncedQ
              ? `Results for "${debouncedQ}"`
              : activeCategory === "All"
              ? "Latest insights"
              : activeCategory}
          </h2>
          <p
            data-testid="insights-result-count"
            className="text-[12px] uppercase tracking-[0.22em] text-mute-300"
          >
            {loading
              ? "Loading…"
              : `${articles.length} article${articles.length === 1 ? "" : "s"}`}
          </p>
        </div>

        {gridArticles.length === 0 && !loading ? (
          <div
            className="mt-10 rounded-3xl glass p-10 sm:p-14 text-center"
            data-testid="insights-empty-state"
          >
            <Newspaper className="w-6 h-6 text-gold mx-auto" />
            <h3 className="mt-5 font-display text-2xl text-mute-100">
              No articles match yet.
            </h3>
            <p className="mt-3 text-[14.5px] text-mute-200 max-w-md mx-auto">
              Try a different category or clear the search to see the full
              library.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gridArticles.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------- COMPONENTS -------------------- */

function CategoryPill({ label, count, active, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`insights-cat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={[
        "inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] tracking-[0.01em] transition-colors backdrop-blur-md",
        "border",
        active
          ? "border-gold/60 bg-gold/[0.10] text-gold"
          : disabled
          ? "border-white/5 bg-white/[0.015] text-mute-300/50 cursor-not-allowed"
          : "border-white/10 bg-white/[0.025] text-mute-100 hover:border-gold/40 hover:text-gold hover:bg-gold/[0.05]",
      ].join(" ")}
    >
      {label}
      {typeof count === "number" && (
        <span
          className={[
            "tabular-nums text-[10.5px] tracking-[0.18em]",
            active ? "text-gold/80" : "text-mute-300",
          ].join(" ")}
        >
          {String(count).padStart(2, "0")}
        </span>
      )}
    </button>
  );
}

function FeaturedCard({ article }) {
  return (
    <Link
      to={`/insights/${article.slug}`}
      data-testid="insights-featured-card"
      className="group relative block rounded-3xl glass-strong overflow-hidden card-hover"
    >
      <div
        className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none opacity-80"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
        }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
        <div className="lg:col-span-7 relative overflow-hidden aspect-[16/10] lg:aspect-auto lg:min-h-[440px]">
          {article.featured_image && (
            <img
              src={article.featured_image}
              alt={article.title}
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
            />
          )}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,12,14,0.45) 0%, rgba(10,12,14,0.20) 50%, rgba(10,12,14,0.80) 100%)",
            }}
          />
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-gold">
              <Sparkles className="w-3.5 h-3.5" /> Featured
            </span>
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-mute-200/90">
              {article.category}
            </span>
          </div>
        </div>

        <div className="lg:col-span-5 p-8 sm:p-10 lg:p-14 flex flex-col">
          <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
            Editor's pick · {formatDate(article.published_at)}
          </span>
          <h2 className="mt-6 font-display text-[26px] sm:text-[32px] lg:text-[36px] leading-[1.12] tracking-tight text-mute-100">
            {article.title}
          </h2>
          <p className="mt-5 text-[15px] leading-[1.75] text-mute-200">
            {article.excerpt}
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-[13.5px] text-gold group-hover:translate-x-0.5 transition-transform">
            Read article
            <ArrowUpRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article, index }) {
  return (
    <Link
      to={`/insights/${article.slug}`}
      data-testid={`insights-card-${index}`}
      className="group relative flex flex-col rounded-3xl glass card-hover overflow-hidden h-full"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {article.featured_image && (
          <img
            src={article.featured_image}
            alt={article.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,12,14,0.40) 0%, rgba(10,12,14,0.10) 40%, rgba(10,12,14,0.75) 100%)",
          }}
        />
        <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-mute-100">
          {article.category}
        </span>
      </div>
      <div className="flex-1 p-7 flex flex-col">
        <div className="flex items-center gap-2 text-[11px] text-mute-300 tracking-[0.05em]">
          <CalendarDays className="w-3.5 h-3.5 text-gold/80" />
          {formatDate(article.published_at)}
        </div>
        <h3 className="mt-4 font-display text-[20px] sm:text-[22px] leading-[1.2] text-mute-100">
          {article.title}
        </h3>
        <p className="mt-3 text-[14px] leading-[1.7] text-mute-200 line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-6 inline-flex items-center gap-1.5 text-[13px] text-gold group-hover:translate-x-0.5 transition-transform">
          Read more
          <ArrowUpRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
